import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SEO_PAGES, LIVE_DOMAIN, getStructuredData, getOptimizedImageUrl } from '../lib/seo';

export default function SEO({ title: customTitle, description: customDescription, image: customImage, canonical: customCanonical, noindex = false, faq = null, extraSchemas = [] }) {
  const { pathname } = useLocation();
  const pageMeta = SEO_PAGES[pathname] || {};

  const title = customTitle || pageMeta.title || 'BM Maler | malerarbejde i særklasse - kvalitet siden 1957';
  const description = customDescription || pageMeta.description || 'Vælg BM Maler, din maler i Vejle siden 1957. Få et flot og holdbart resultat med grundigt forarbejde, moderne teknik og stolte håndværkstraditioner.';
  
  const rawImage = customImage || pageMeta.ogImage || 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/a4ab1f20-dc6d-4e64-b7ae-1e79f8e81f16.jpg';
  const ogImageUrl = getOptimizedImageUrl(rawImage, { width: 1200, quality: 85 });

  const canonicalUrl = customCanonical 
    ? (customCanonical.startsWith('http') ? customCanonical : `${LIVE_DOMAIN}${customCanonical}`)
    : (pageMeta.canonical 
        ? (pageMeta.canonical.startsWith('http') ? pageMeta.canonical : `${LIVE_DOMAIN}${pageMeta.canonical}`)
        : `${LIVE_DOMAIN}${pathname}`);
  const structuredDataList = [
    ...getStructuredData(pathname, title, description, faq),
    ...extraSchemas,
  ];

  // Consolidate all schemas into a single clean @graph to prevent deduplication and escaping issues
  const graphSchema = {
    '@context': 'https://schema.org',
    '@graph': structuredDataList.map((schema) => {
      const { '@context': _, ...cleanSchema } = schema;
      return cleanSchema;
    }),
  };

  return (
    <Helmet>
      {/* Primary HTML Title & Description */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Search Engine Robots Indexing */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content="BM Maler A/S" />
      <meta property="og:type" content={pageMeta.ogType || 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:locale" content="da_DK" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD Structured Data Schema (@graph format with dangerouslySetInnerHTML to prevent escaping bugs) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(graphSchema) }}
      />
    </Helmet>
  );
}
